import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  ADD_ACTIVITY,
  getApiBaseUrl,
  LOGIN,
  GET_ROLES,
} from "../AssetMgt/Config/Config";
import { Spin } from "antd";

const NewUserRequests = () => {
  const [userData, setUserData] = useState({
    Name: "",
    Role: "",
    UserId: "",
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}${GET_ROLES}`);
        const data = await response.json();
        if (data.Status) {
          setRoles(data.ResultData);
        } else {
          throw new Error("Failed to load roles.");
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        setError("Failed to load roles. Please try again.");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load roles. Please try again.",
        });
      }
    };
    fetchRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear error on input change
  };

  const handleSubmit = async () => {
    if (!userData.Name || !userData.Role || !userData.UserId) {
      setError("Please fill in all required fields: UserId, Name, and Role.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in all required fields: UserId, Name, and Role.",
      });
      return;
    }

    const user = JSON.parse(sessionStorage.getItem("user"));
    const createdBy = user?.UserName || "System";
    const payload = {
      Name: userData.Name,
      UserId: userData.UserId,
      Role: userData.Role,
      CreatedUser: createdBy,
    };

    setLoading(true);
    setError(""); // Clear previous errors

    let registerSucceeded = false;
    let registerMessage = "User request sent successfully!";

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/addRequestUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      // Check if the API response indicates success
      if (result.Status && result.ResultData?.[0]?.Status === "Success") {
        registerSucceeded = true;
        registerMessage = "User request sent successfully!";
      } else {
        registerMessage = "Something went wrong.";
        throw new Error(registerMessage);
      }
    } catch (error) {
      registerMessage = error.message || "Unable to connect to the server.";
      setError(registerMessage);
    } finally {
      setLoading(false);

      // Log activity
      try {
        const userId = user?.UserID || 0;
        const activityPayload = {
          ActivityType: 6,
          PerformedBy: userId,
          PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
          Notes: registerSucceeded
            ? `New User request successful for ${userData.UserId}`
            : `New User request failed for ${userData.UserId}: ${registerMessage}`,
          Location: "Dashboard",
          IsSucces: registerSucceeded ? 1 : 0,
        };

        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(activityPayload),
        });
      } catch (error) {
        console.error("Error calling /addActivity:", error);
        setError("Failed to log activity. Please try again.");
      }

      if (registerSucceeded) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: registerMessage,
          confirmButtonText: "OK",
        }).then(() => {
          window.location.reload();
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: registerMessage,
        });
      }
    }
  };

  const clearUserData = () => {
    setUserData({ Name: "", Role: "", UserId: "" });
    setAskPassword(false);
    setPassword("");
    setError("");
  };

  useEffect(() => {
    const offcanvas = document.getElementById("offcanvasAddUserRewquest");
    if (offcanvas) {
      const handleClose = () => {
        clearUserData();
      };
      offcanvas.addEventListener("hidden.bs.offcanvas", handleClose);
      return () => {
        offcanvas.removeEventListener("hidden.bs.offcanvas", handleClose);
      };
    }
  }, []);

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError(""); // Clear previous errors

    const user = JSON.parse(sessionStorage.getItem("user"));

    try {
      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await handleSubmit();
      } else {
        throw new Error(
          data.Message || "Invalid credentials. Please try again.",
        );
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div>
      <style>
        {`
        @media (max-width: 576px) {
          #offcanvasAddUserRewquest {
            width: 90% !important;
          }
        }

        @media (min-width: 577px) and (max-width: 992px) {
          #offcanvasAddUserRewquest {
            width: 55% !important;
          }
        }

        @media (min-width: 993px) and (max-width: 1440px) {
          #offcanvasAddUserRewquest {
            width: 35% !important;
          }
        }
          
        @media (min-width: 1441px) {
          #offcanvasAddUserRewquest {
            width: 20% !important;
          }
        }
        `}
      </style>
      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvasAddUserRewquest"
        aria-labelledby="offcanvasAddUserRewquest"
      >
        <form>
          <div className="offcanvas-header d-flex justify-content-between align-items-center w-100">
            <h5 id="offcanvasAddUserRewquest">Request A New User</h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className={`btn btn-sm btn-outline-success me-2 ${
                  askPassword ? "d-none" : ""
                }`}
                title="Save"
                onClick={(e) => {
                  e.preventDefault();
                  if (!userData.Name || !userData.Role || !userData.UserId) {
                    setError(
                      "Please fill in all required fields: UserId, Name, and Role.",
                    );
                    Swal.fire({
                      icon: "warning",
                      title: "Validation Error",
                      text: "Please fill in all required fields: UserId, Name, and Role.",
                    });
                    return;
                  }
                  setAskPassword(true);
                }}
                disabled={loading}
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
              ></button>
            </div>
          </div>
          <hr className="m-0" />
          <div className="offcanvas-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label">User ID</label>
                <span className="text-danger ms-1">*</span>
                <input
                  type="text"
                  name="UserId"
                  className="form-control"
                  placeholder="Enter User ID"
                  value={userData.UserId}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  required
                  disabled={loading || askPassword}
                />
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">Name</label>
                <span className="text-danger ms-1">*</span>
                <input
                  type="text"
                  name="Name"
                  className="form-control"
                  placeholder="Enter Name"
                  value={userData.Name}
                  onChange={handleChange}
                  required
                  disabled={loading || askPassword}
                />
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">Role</label>
                <span className="text-danger ms-1">*</span>
                <select
                  name="Role"
                  className="form-control"
                  value={userData.Role}
                  onChange={handleChange}
                  required
                  disabled={loading || askPassword}
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {askPassword && (
              <div className="mt-2">
                <div className="mb-3 w-lg-75 m-auto">
                  <label className="form-label">
                    Confirm Password <span className="text-danger"> *</span>
                  </label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(""); // Clear error on password change
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        showPassword ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    ></span>
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleAuth}
                  disabled={authLoading || !password}
                >
                  {authLoading ? "Verifying..." : "Confirm"}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setAskPassword(false);
                    setPassword("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewUserRequests;
